import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banner, Button, Card, PageHead, Skeleton, StateBlock } from '@w7/ui'
import { useCreateAccount } from '@/api/hooks/create/useCreateAccount'
import { useCreateParent } from '@/api/hooks/create/useCreateParent'
import { checkUsernameAvailable } from '@/api/hooks/create/useUsernameAvailable'
import { AccountLink, Amount, FlagChips } from '@/app/bits'
import { EMPTY, lll, money, pct } from '@/lib/format'
import { ROLE_LABEL, creatableRoles } from '@/data/types'
import type { Account, Role } from '@/data/types'
import type * as api from '@/data/api'
import {
  CreditSection,
  GamesSection,
  RatesSection,
  ReviewSection,
  RoleIdentitySection,
  SECTIONS,
  type SectionCtx,
} from './sections'
import { CreateSubmit, SummaryRail, type ChecklistEntry } from './SummaryRail'
import {
  betLimitErrors,
  commissionError,
  creditError,
  deriveSummary,
  parentMaxBet,
  parseNumber,
  shareError,
  usernameError,
  usernameFormatError,
  type Availability,
  type CreateValues,
} from './summary'
import './create.css'

/**
 * Add to downline — one page.
 *
 * Two products are being replaced here. The first was a single wall of about
 * forty inputs: FIVE per-level credit-limit boxes (three of which never applied
 * to the role you had picked), the password rules already red before anything
 * was typed, and a washed-out "Save" beside a bright red "Cancel". The second
 * was this console's own four-step wizard, which fixed all of that but bought
 * the fix with gating — four Next buttons, each greyed until its step was
 * satisfied, and no way to see what the terms added up to until step four.
 *
 * This is the wizard's content on one page: five sections down the left, a
 * sticky rail on the right stating the live consequence of what has been typed,
 * and a checklist that navigates instead of blocking. Not one validation rule,
 * cap or explanatory sentence was dropped in the collapse — only the stepper.
 */

const INITIAL: CreateValues = {
  role: null,
  username: '',
  displayName: '',
  comments: '',
  creditLimit: '',
  share: '0',
  fancy: '0',
  bookmaker: '0',
  matchOdds: '0',
  minBet: '100',
  maxBet: '',
  games: {
    casino: true,
    cardGame: true,
    liveGame: true,
    sportsbook: true,
    premiumSportsbook: false,
  },
}

/** Document order. Drives the error summary and "focus the FIRST invalid control". */
const FIELD_ORDER = [
  'username',
  'creditLimit',
  'share',
  'fancy',
  'bookmaker',
  'matchOdds',
  'minBet',
  'maxBet',
] as const

const FIELD_LABEL: Record<(typeof FIELD_ORDER)[number], string> = {
  username: 'Username',
  creditLimit: 'Credit limit',
  share: 'Their share',
  fancy: 'Fancy commission',
  bookmaker: 'Bookmaker commission',
  matchOdds: 'Match odds commission',
  minBet: 'Minimum bet',
  maxBet: 'Maximum bet',
}

/** Respect the OS setting — an anchor jump that animates past three cards is nausea, not polish. */
function scrollBehaviour(): ScrollBehavior {
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return reduced ? 'auto' : 'smooth'
}

function buildPayload(values: CreateValues, role: Role, parent: Account): api.CreateAccountInput {
  const isMember = role === 'player'
  return {
    role,
    username: values.username.trim(),
    displayName: values.displayName.trim() || values.username.trim(),
    creditLimit: parseNumber(values.creditLimit) ?? 0,
    share: parseNumber(values.share) ?? 0,
    commission: {
      fancy: parseNumber(values.fancy) ?? 0,
      bookmaker: parseNumber(values.bookmaker) ?? 0,
      matchOdds: parseNumber(values.matchOdds) ?? 0,
    },
    // Bet limits and game availability are member-level settings. An agent
    // inherits the creator's rather than being asked for numbers that will
    // never be applied to it.
    betLimits: isMember
      ? { min: parseNumber(values.minBet) ?? 0, max: parseNumber(values.maxBet) ?? 0 }
      : { ...parent.betLimits },
    games: isMember ? values.games : { ...parent.games },
    comments: values.comments.trim() || undefined,
  }
}

/* ------------------------------------------------------------------ page -- */

export function CreateAccountPage() {
  const navigate = useNavigate()
  const { data: account, isLoading, isError, error, refetch } = useCreateParent()

  if (isLoading) {
    return (
      <>
        <PageHead title="Add to downline" subtitle="Loading your limits and rates" />
        <div className="w7-col" style={{ gap: 'var(--w7-s5)', maxWidth: 720 }}>
          {['a', 'b', 'c'].map((key) => (
            <Card key={key}>
              <div className="w7-col" style={{ gap: 'var(--w7-s5)' }}>
                <Skeleton width={140} height={12} />
                <Skeleton height={38} radius={10} />
                <Skeleton height={38} radius={10} />
              </div>
            </Card>
          ))}
        </div>
      </>
    )
  }

  if (isError || !account) {
    return (
      <StateBlock
        icon="error"
        tone="error"
        title="Your own limits could not be loaded"
        actions={
          <Button variant="secondary" icon="refresh" onClick={() => refetch()}>
            Try again
          </Button>
        }
      >
        {(error as Error)?.message ??
          'Nothing can be created until the console knows your balance, share and bet limits — every value on this form is capped by one of them. Live login needs hierarchy on :3910 (`npm run hierarchy:mock` or `npm run dev:local`).'}
      </StateBlock>
    )
  }

  const [first, ...rest] = creatableRoles(account.role)

  if (!first) {
    return (
      <>
        <PageHead title="Add to downline" />
        <StateBlock
          icon="downline"
          title="There is no level beneath you to add"
          actions={
            <Button variant="secondary" icon="overview" onClick={() => navigate('/')}>
              Back to overview
            </Button>
          }
        >
          You are signed in as a {ROLE_LABEL[account.role]}, which sits at the bottom of the ladder,
          so there is no level to create underneath. If you should be able to add agents or members,
          the agent above you controls that.
        </StateBlock>
      </>
    )
  }

  // Split so the form's hooks never sit behind a conditional return, and so the
  // form can treat "there is a creatable role" as a type-level fact.
  return <CreateForm account={account} roles={[first, ...rest]} />
}

/* ------------------------------------------------------------------ form -- */

function CreateForm({ account, roles }: { account: Account; roles: [Role, ...Role[]] }) {
  const navigate = useNavigate()
  const create = useCreateAccount()

  const [values, setValues] = useState<CreateValues>(INITIAL)
  const [answered, setAnswered] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(false)
  const [availability, setAvailability] = useState<Availability>('idle')
  /** Create was pressed while the username probe was still in flight. */
  const [awaitingProbe, setAwaitingProbe] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('role')

  const controls = useRef(new Map<string, HTMLElement | null>())
  const registrars = useRef(new Map<string, (el: HTMLElement | null) => void>())

  const role = values.role ?? roles[0]
  const isMember = role === 'player'
  const created = create.data
  const summary = deriveSummary(values, account)

  /* ------------------------------------------------------------ validity -- */

  const bets = betLimitErrors(
    parseNumber(values.minBet),
    parseNumber(values.maxBet),
    parentMaxBet(account),
  )

  const errors: Record<string, string | null> = {
    username: usernameError(values.username, availability),
    creditLimit: creditError(parseNumber(values.creditLimit), account.balance),
    share: shareError(parseNumber(values.share), account.share.sportsbook),
    fancy: commissionError(parseNumber(values.fancy), account.commission.fancy, 'fancy'),
    bookmaker: commissionError(
      parseNumber(values.bookmaker),
      account.commission.bookmaker,
      'bookmaker',
    ),
    matchOdds: commissionError(
      parseNumber(values.matchOdds),
      account.commission.matchOdds,
      'match odds',
    ),
    minBet: isMember ? bets.min : null,
    maxBet: isMember ? bets.max : null,
  }

  const outstanding = FIELD_ORDER.flatMap((field) => {
    const message = errors[field]
    return message ? [{ field, message }] : []
  })

  /**
   * An in-flight probe is not an error, it is a wait. If it is the only thing
   * left, Create goes busy and fires itself the moment the answer lands — which
   * deletes the wizard's slightly odd "Checking whether that username is free"
   * block reason.
   */
  const probePending = availability === 'checking' && usernameFormatError(values.username) === null

  /* ------------------------------------------------------------- effects -- */

  // Availability is probed while typing, not on submit, because discovering a
  // clash after filling the whole form is the worst possible moment.
  useEffect(() => {
    const name = values.username.trim()
    if (usernameFormatError(name) !== null) {
      setAvailability('idle')
      return
    }
    setAvailability('checking')
    let live = true
    const timer = window.setTimeout(() => {
      checkUsernameAvailable(name)
        .then((free) => {
          if (live) setAvailability(free ? 'free' : 'taken')
        })
        .catch(() => {
          if (live) setAvailability('idle')
        })
    }, 300)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [values.username])

  // The deferred submit. Re-runs on every value change too, but `awaitingProbe`
  // is false by then, so it cannot fire twice.
  useEffect(() => {
    if (!awaitingProbe || availability === 'checking') return
    setAwaitingProbe(false)
    // 'taken' now renders as a field error; anything else (free, or a probe that
    // failed outright) goes through — the server enforces uniqueness regardless.
    if (availability === 'taken') return
    create.mutate(buildPayload(values, role, account))
  }, [awaitingProbe, availability, create, values, role, account])

  // The anchor rail's "you are here". Purely navigational — it gates nothing.
  useEffect(() => {
    if (created || typeof IntersectionObserver === 'undefined') return
    const els = SECTIONS.filter((section) => !section.memberOnly || isMember)
      .map((section) => document.getElementById(section.id))
      .filter((el): el is HTMLElement => el !== null)
    if (els.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) setActiveSection(entry.target.id)
      },
      { rootMargin: '-40% 0px -55% 0px' },
    )
    for (const el of els) observer.observe(el)
    return () => observer.disconnect()
  }, [created, isMember])

  /* ------------------------------------------------------------- plumbing -- */

  const set = <K extends keyof CreateValues>(key: K, value: CreateValues[K]) => {
    // Never clears anything else. Switching Member -> agent hides the games and
    // bet-limit section but keeps every value typed into it, so exploring a
    // different level destroys no work — a real annoyance in the form this
    // replaces, which wiped the section on the radio change.
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  /**
   * Blur marks a field answered ONLY when it carries a value, so tabbing through
   * a blank form paints nothing red while leaving a half-typed username does
   * show the format error.
   *
   * The rule is asymmetric on the way back: an error clears the instant the
   * value becomes valid, without waiting for another blur. That half falls out
   * of `errorFor` recomputing every render, and it is deliberate — showing an
   * error is a considered act, clearing one is not.
   */
  const answer = (field: string, value: string) => {
    if (value.trim() === '') return
    setAnswered((prev) => (prev[field] ? prev : { ...prev, [field]: true }))
  }

  const errorFor = (field: string) => (answered[field] ? (errors[field] ?? null) : null)

  /** Stable per field, so an input's ref does not detach and reattach on every keystroke. */
  const register = (field: string) => {
    let fn = registrars.current.get(field)
    if (!fn) {
      fn = (el: HTMLElement | null) => {
        controls.current.set(field, el)
      }
      registrars.current.set(field, fn)
    }
    return fn
  }

  const focusField = (field: string) => {
    const el = controls.current.get(field)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: scrollBehaviour() })
    el.focus({ preventScroll: true })
  }

  const goToSection = (id: string) => {
    const section = document.getElementById(id)
    section?.scrollIntoView({ block: 'start', behavior: scrollBehaviour() })
    const meta = SECTIONS.find((item) => item.id === id)
    const control = meta?.firstControl ? controls.current.get(meta.firstControl) : null
    ;(control ?? section)?.focus({ preventScroll: true })
  }

  /**
   * Create is never disabled, so pressing it on an incomplete form has to DO
   * something: answer every field, render the error summary, and move focus to
   * the first invalid control. On a wizard, disabling Next was defensible
   * because there was somewhere to advance to; on one page there is not.
   */
  const submit = () => {
    if (create.isPending || awaitingProbe) return
    setSubmitted(true)
    setAnswered(Object.fromEntries(FIELD_ORDER.map((field) => [field, true])))

    const firstBad = outstanding[0]
    if (firstBad) {
      // The banner is for orientation; focus is for repair.
      focusField(firstBad.field)
      return
    }
    if (probePending) {
      setAwaitingProbe(true)
      return
    }
    create.mutate(buildPayload(values, role, account))
  }

  const startAnother = () => {
    create.reset()
    // Identity only. Operators create ten members in a row on identical terms,
    // and creating an account moves no money, so keeping the credit limit is
    // safe as well as convenient.
    setValues((prev) => ({ ...prev, username: '', displayName: '', comments: '' }))
    setAvailability('idle')
    setAnswered({})
    setSubmitted(false)
    setAwaitingProbe(false)
  }

  /* ------------------------------------------------------------- success -- */

  if (created) {
    const recorded: { label: string; value: ReactNode }[] = [
      { label: 'Level', value: ROLE_LABEL[created.role] },
      { label: 'Display name', value: created.displayName || EMPTY },
      { label: 'Credit limit', value: <Amount value={created.creditLimit} /> },
      { label: 'Balance', value: <Amount value={created.balance} /> },
      { label: 'Share', value: pct(created.share.sportsbook, 2) },
      {
        label: 'Bet limits',
        value:
          created.role === 'player'
            ? `${money(created.betLimits.min, { round: true })} – ${money(created.betLimits.max, { round: true })}`
            : 'Set per member, not on an agent',
      },
      { label: 'Created', value: lll(created.createdAt) },
    ]

    return (
      <>
        <PageHead title="Add to downline" subtitle={`Under ${account.username}`} />
        <div style={{ width: '100%' }}>
          <Banner
            tone="info"
            title={`${created.username} was created as a ${ROLE_LABEL[created.role]}`}
            actions={
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/member/${created.id}`)}
                >
                  Open member page
                </Button>
                <Button size="sm" variant="primary" icon="plus" onClick={startAnother}>
                  Create another
                </Button>
              </>
            }
          >
            The hierarchy record exists and sits directly under you — open{' '}
            <AccountLink id={created.id} username={created.username} /> to give credit. Sign-in
            provisioning does not exist yet (issue #149), so nobody can log in as this account until
            it ships. Terms are kept for the next one — only the name is cleared.
          </Banner>

          <Card
            title={created.username}
            subtitle="What was recorded"
            style={{ marginTop: 'var(--w7-s5)' }}
            actions={<FlagChips flags={created.flags} />}
          >
            <dl className="w7-dl">
              {recorded.map((row) => (
                <div key={row.label} style={{ display: 'contents' }}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </div>
      </>
    )
  }

  /* -------------------------------------------------------------- render -- */

  const ctx: SectionCtx = {
    parent: account,
    role,
    roles,
    values,
    set,
    answer,
    errorFor,
    register,
    availability,
    summary,
  }

  const visibleSections = SECTIONS.filter((section) => !section.memberOnly || isMember)

  const checklist: ChecklistEntry[] = visibleSections.map((section) => {
    if (section.id === 'review') {
      return {
        id: section.id,
        label: section.label,
        state: outstanding.length === 0 ? 'done' : submitted ? 'error' : 'todo',
      }
    }
    const bad = section.fields.filter((field) => errors[field])
    if (bad.length === 0) return { id: section.id, label: section.label, state: 'done' }
    // Unanswered is "todo", not "error" — the checklist reports progress, it does
    // not accuse you of a mistake you have not had the chance to make yet.
    const shown = submitted || bad.some((field) => answered[field])
    return { id: section.id, label: section.label, state: shown ? 'error' : 'todo' }
  })

  const submitProps = {
    label: awaitingProbe ? 'Checking username…' : `Create ${ROLE_LABEL[role]}`,
    busy: create.isPending || awaitingProbe,
    blockReason: awaitingProbe
      ? null
      : (outstanding.find((item) => submitted || answered[item.field])?.message ?? null),
  }

  const reviewBanners = (
    <>
      {submitted && outstanding.length > 0 ? (
        <Banner
          tone="error"
          title={`${outstanding.length} ${outstanding.length === 1 ? 'thing needs' : 'things need'} fixing before this account can be created`}
        >
          <ul className="w7-create-errlist">
            {outstanding.map((item) => (
              <li key={item.field}>
                <button type="button" onClick={() => focusField(item.field)}>
                  {FIELD_LABEL[item.field]} — {item.message}
                </button>
              </li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <Banner tone="warn" title="No sign-in is created">
        This records the hierarchy entry only. Sign-in provisioning does not exist yet (issue #149),
        so {values.username.trim() || 'this account'} cannot log in; it will carry a "No login yet"
        marker until provisioning ships.
      </Banner>

      {create.isError ? (
        <Banner tone="error" title="The account was not created">
          {(create.error as Error)?.message ??
            'The request failed before anything was recorded. Nothing beneath you has changed.'}
        </Banner>
      ) : null}
    </>
  )

  return (
    <>
      <PageHead
        title="Add to downline"
        subtitle={`Creating directly under ${account.username} · ${ROLE_LABEL[account.role]}`}
        actions={
          // Quiet, secondary and out of the way. The original put a bright red
          // Cancel beside a washed-out Save, which is the emphasis exactly
          // backwards.
          <Button variant="ghost" onClick={() => navigate('/downline')}>
            Cancel
          </Button>
        }
      />

      {/* One form, so Enter in any text field submits. That is acceptable HERE
          specifically because creating an account moves no money and is not
          destructive — the worst case of a stray Enter is the error summary.
          Do not "fix" this in either direction without that reasoning. */}
      <form
        className="w7-create"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <div className="w7-create-main">
          <Banner tone="info" title="This creates the hierarchy record only">
            Posted to hierarchy-service <code>POST /v1/agents</code> with name, credit limit, share,
            commission and member flag. Username, games, bet limits and comments stay on this form
            only until those fields ship on the API. Sign-in provisioning does not exist yet (issue
            #149) — nobody can log in as the new account until that ships.
          </Banner>

          <RoleIdentitySection ctx={ctx} />
          <CreditSection ctx={ctx} />
          <RatesSection ctx={ctx} />
          {/* Absent, not greyed: bet limits and game switches are member-level
              settings and would be meaningless on an agent. */}
          {isMember ? <GamesSection ctx={ctx} /> : null}
          <ReviewSection ctx={ctx} banner={reviewBanners} />
        </div>

        <SummaryRail ctx={ctx} checklist={checklist} onNavigate={goToSection} submit={submitProps} />

        <div className="w7-create-actionbar">
          <CreateSubmit {...submitProps} />
        </div>

        <nav className="w7-create-nav" aria-label="Sections of this form">
          <ul className="w7-create-navlist">
            {visibleSections.map((section) => (
              <li key={section.id}>
                <a
                  className="w7-create-navlink"
                  href={`#${section.id}`}
                  aria-current={activeSection === section.id ? 'true' : undefined}
                  onClick={(event) => {
                    event.preventDefault()
                    goToSection(section.id)
                  }}
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </form>
    </>
  )
}
